import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Chip, Paper, Typography, CircularProgress, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import AlertDialog from '../AlertDialog';
import TOBTable from './TOBTable';
import MenuBar from './TOBTableMenuBar';
import { patchCell, patchCellStyle, readCell, isBoldCell } from '../../utils/tobTableEditing';
import {
    fetchCustomTobPlans,
    createCustomTobPlan,
    updateCustomTobPlan,
    deleteCustomTobPlan,
    updateCustomTobPlanSortingOrder,
} from '../../services/CustomTOBService';

const PLAN_COLUMN_WIDTH = 200;
const CUSTOM_TOB_VERSION = '1.0';
const SAVE_STATUS_DELAY_MS = 1000;

// Row 1 is the navy plan-name row and these are the sky blue section header rows -- every
// other row is a plain benefit row that should start white in a new column regardless of
// whatever background colour happens to be on the label cell.
const SKY_BLUE_HEADER_ROWS = new Set([6, 18, 29, 42, 48, 53]);

// A blank column still has to line up with the benefit labels, so it is seeded with an
// empty cell per row that keeps the row's colours -- that is what makes the section
// headers and the navy plan-name row read the same as in the main Table of Benefits.
function createBlankTobPlan(labelsColumn, planNumber) {
    const table = {};

    Object.keys(labelsColumn || {}).forEach(rowKey => {
        const rowData = labelsColumn[rowKey];
        const labelCell = rowData?.['1'] || rowData || {};
        const rowNum = Number(rowKey);
        const isHeaderRow = rowNum === 1 || SKY_BLUE_HEADER_ROWS.has(rowNum);

        table[rowKey] = {
            '1': {
                cell_value: '',
                cell_bg_color: isHeaderRow ? labelCell.cell_bg_color : undefined,
                cell_text_color: labelCell.cell_text_color,
                font_weight: labelCell.font_weight,
            },
        };
    });

    return { plan_id: `custom_tob_${uuidv4()}`, label: `Custom Plan ${planNumber}`, table };
}

// Row 1 is the insurer/plan header and row 2 the plan type, so together they read the way
// the plan is normally referred to -- "Lifeline" + "Essential" -> "Lifeline Essential".
function composedPlanName(plan) {
    return ['1', '2']
        .map(rowNum => (plan.table?.[rowNum]?.['1']?.cell_value || '').trim())
        .filter(Boolean)
        .join(' ');
}

function planDisplayName(plan) {
    return (plan.custom_label || '').trim() || composedPlanName(plan) || plan.label;
}

function SortableCustomTobChip({ plan, onDelete, onRename }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: plan.plan_id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        // As a plain block the wrapper takes the dialog title's tall line-height and sits
        // the chip lower than the Add TOB chip beside it.
        display: 'flex',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <Chip size="small"
                label={planDisplayName(plan)}
                onClick={onRename}
                onDelete={onDelete}
                sx={{ backgroundColor: '#7e57c2', color: '#fff', cursor: 'inherit', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } } }}
            />
        </div>
    );
}

const defaultLayout = ({ header, content }) => (
    <>
        {header}
        {content}
    </>
);

// The form is hosted both in a dialog and on its own page, so the host decides where the
// header (title, chips, toolbar) and the table go through renderLayout. `active` is what
// triggers a reload of the saved plans -- the dialog ties it to being open. A host that
// caches the plans passes them as initialPlans and gets every change back through
// onPlansChange, so the form shows the cache at once and refreshes it behind the scenes.
// fillHeight makes the table fill a flex column host and scroll inside it.
export default function CustomTobPlanForm({ active = true, tobBenefitLabelsColumn, visibleRowNumbers, loadingTobBenefits, token, titleProps, renderLayout = defaultLayout, initialPlans, onPlansChange, fillHeight = false }) {
    const [labelsColumn, setLabelsColumn] = useState(tobBenefitLabelsColumn || null);
    const [customTobPlans, setCustomTobPlans] = useState(() => initialPlans || []);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [renamingPlan, setRenamingPlan] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [planPendingDelete, setPlanPendingDelete] = useState(null);
    // { planUid, rows: ['7', '8', ...] } -- planUid holds a plan's plan_id, always within one column.
    const [selection, setSelection] = useState(null);

    // 'saving' while any request is in flight, 'saved' once the last one succeeds.
    const [saveStatus, setSaveStatus] = useState(null);
    const pendingSaves = useRef(0);
    const saveFailed = useRef(false);
    // Bumped by every save, so a reload that started before an edit knows it is stale.
    const editCount = useRef(0);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    // Saves go out immediately, but the indicator waits for a quiet spell so that moving
    // quickly from cell to cell does not keep flashing "Saving...".
    const statusDelayTimer = useRef(null);
    useEffect(() => () => clearTimeout(statusDelayTimer.current), []);

    // Edits fire saves back to back, so the indicator only settles once every one of them
    // has come back -- a failure in the batch drops it rather than claiming "Saved".
    function refreshSaveStatus() {
        if (statusDelayTimer.current) return;
        if (pendingSaves.current > 0) {
            setSaveStatus('saving');
            return;
        }
        setSaveStatus(saveFailed.current ? null : 'saved');
        saveFailed.current = false;
    }

    function trackSave(request) {
        pendingSaves.current += 1;
        editCount.current += 1;
        setSaveStatus(null);

        clearTimeout(statusDelayTimer.current);
        statusDelayTimer.current = setTimeout(() => {
            statusDelayTimer.current = null;
            refreshSaveStatus();
        }, SAVE_STATUS_DELAY_MS);

        return request
            .catch(error => {
                saveFailed.current = true;
                throw error;
            })
            .finally(() => {
                pendingSaves.current -= 1;
                refreshSaveStatus();
            });
    }

    // Nothing can be added or formatted until the benefit labels a column is built from land.
    const ready = !(loadingTobBenefits && !labelsColumn) && !loadingPlans;

    // The labels are fetched lazily by the page, so they can arrive after the dialog is
    // already open. Only the first arrival seeds the form -- later ones would discard edits.
    useEffect(() => {
        if (!tobBenefitLabelsColumn) return;
        setLabelsColumn(prev => prev || tobBenefitLabelsColumn);
    }, [tobBenefitLabelsColumn]);

    // Each time the form becomes active it reloads the saved plans, so edits made (and saved)
    // the last time it was open are reflected instead of stale local state. Only the very
    // first load blocks the form -- later opens keep showing what is already on screen while
    // the refresh happens quietly behind it. Cached plans count as that first load.
    const hasLoadedOnce = useRef(Boolean(initialPlans));
    useEffect(() => {
        if (!active) return;

        const isFirstLoad = !hasLoadedOnce.current;
        if (isFirstLoad) setLoadingPlans(true);

        // The form is usable while a quiet refresh is in flight, and anything edited in the
        // meantime is newer than what the server sends back for this request.
        const editCountAtStart = editCount.current;

        fetchCustomTobPlans({ token })
            .then(response => {
                if (editCount.current !== editCountAtStart) return;
                setCustomTobPlans(response.data.custom_tob_plans || []);
                hasLoadedOnce.current = true;
            })
            .catch(() => toast.error('Failed to load custom TOB plans.'))
            .finally(() => { if (isFirstLoad) setLoadingPlans(false); });
    }, [active, token]);

    // The empty list a first visit starts from is not what the user has, so it must not be
    // handed back as if it were.
    useEffect(() => {
        if (!hasLoadedOnce.current) return;
        onPlansChange?.(customTobPlans);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customTobPlans]);

    // Every edit (cell, formatting, merge, rename) sends the whole plan back so the server
    // row never drifts from what is on screen.
    async function persistPlan(plan) {
        if (!plan.id) return;

        try {
            await trackSave(updateCustomTobPlan({
                id: plan.id,
                body: {
                    plan_id: plan.plan_id,
                    label: planDisplayName(plan),
                    table: plan.table,
                    vertical_merge_cells: plan.vertical_merge_cells || [],
                },
                token,
            }));
        } catch (error) {
            toast.error('Failed to save changes to the TOB plan.');
        }
    }

    function handleAddColumn() {
        if (!labelsColumn) return;

        const blankPlan = createBlankTobPlan(labelsColumn, customTobPlans.length + 1);
        setCustomTobPlans(prev => [...prev, blankPlan]);

        trackSave(createCustomTobPlan({
            body: {
                plan_id: blankPlan.plan_id,
                label: blankPlan.label,
                table: blankPlan.table,
                vertical_merge_cells: [],
                version: CUSTOM_TOB_VERSION,
            },
            token,
        }))
            // The server row carries the DB id everything else (update, delete, reorder) needs.
            .then(response => {
                setCustomTobPlans(prev => prev.map(p => (p.plan_id === blankPlan.plan_id ? response.data.custom_tob_plan : p)));
            })
            .catch(() => {
                toast.error('Failed to add TOB plan.');
                setCustomTobPlans(prev => prev.filter(p => p.plan_id !== blankPlan.plan_id));
            });
    }

    function handleRequestDeleteColumn(plan) {
        setPlanPendingDelete(plan);
    }

    async function handleConfirmDeleteColumn() {
        const plan = planPendingDelete;
        if (!plan) return;

        const previousPlans = customTobPlans;
        setCustomTobPlans(prev => prev.filter(p => p.plan_id !== plan.plan_id));
        setSelection(prev => (prev?.planUid === plan.plan_id ? null : prev));

        try {
            await trackSave(deleteCustomTobPlan({ id: plan.id, token }));
        } catch (error) {
            toast.error('Failed to delete TOB plan.');
            setCustomTobPlans(previousPlans);
        }
    }

    function handleUpdateCell(planUid, rowNum, value) {
        const plan = customTobPlans.find(p => p.plan_id === planUid);
        if (!plan) return;

        // The table commits on every blur, so leaving a cell untouched must not cost a save.
        if (String(readCell(plan.table, rowNum)?.cell_value ?? '') === String(value ?? '')) return;

        const updatedPlan = { ...plan, table: patchCell(plan.table, rowNum, { cell_value: value }) };
        setCustomTobPlans(prev => prev.map(p => (p.plan_id === planUid ? updatedPlan : p)));
        persistPlan(updatedPlan);
    }

    // The toolbar shows the state of the first cell in the selection and applies it to all.
    const selectedCellData = selection
        ? readCell(customTobPlans.find(plan => plan.plan_id === selection.planUid)?.table, selection.rows[0])
        : null;

    function formatSelection(patch) {
        if (!selection) return;

        const plan = customTobPlans.find(p => p.plan_id === selection.planUid);
        if (!plan) return;

        const table = selection.rows.reduce((acc, rowNum) => patchCellStyle(acc, rowNum, patch), plan.table);
        const updatedPlan = { ...plan, table };
        setCustomTobPlans(prev => prev.map(p => (p.plan_id === updatedPlan.plan_id ? updatedPlan : p)));
        persistPlan(updatedPlan);
    }

    const selectedPlanUid = selection?.planUid || null;
    const selectionRange = selection && {
        start: Math.min(...selection.rows.map(Number)),
        end: Math.max(...selection.rows.map(Number)),
    };
    const overlappingMerges = selectedPlanUid
        ? (customTobPlans.find(plan => plan.plan_id === selectedPlanUid)?.vertical_merge_cells || [])
            .filter(merge => merge.end_row >= selectionRange.start && merge.start_row <= selectionRange.end)
        : [];

    const canMerge = Boolean(selectedPlanUid) && selection.rows.length > 1;
    const canUnmerge = overlappingMerges.length > 0;

    // Anything the new span overlaps is replaced by it, so the ranges never interleave.
    function updateMerges(updater) {
        const plan = customTobPlans.find(p => p.plan_id === selectedPlanUid);
        if (!plan) return;

        const kept = (plan.vertical_merge_cells || []).filter(merge => (
            merge.end_row < selectionRange.start || merge.start_row > selectionRange.end
        ));

        const updatedPlan = { ...plan, vertical_merge_cells: updater(kept) };
        setCustomTobPlans(prev => prev.map(p => (p.plan_id === selectedPlanUid ? updatedPlan : p)));
        persistPlan(updatedPlan);
    }

    function handleMerge() {
        if (!canMerge) return;

        updateMerges(kept => [...kept, { col: 1, start_row: selectionRange.start, end_row: selectionRange.end }]
            .sort((a, b) => a.start_row - b.start_row));

        // Only the first row of a merge is rendered, so the rest can no longer be clicked.
        setSelection({ planUid: selectedPlanUid, rows: [String(selectionRange.start)] });
    }

    function handleUnmerge() {
        if (!canUnmerge) return;
        // updateMerges already drops every span the selection touches, so keeping only what
        // it hands back is the unmerge.
        updateMerges(kept => kept);
    }

    // The chips are the columns, so their order is the column order.
    async function handleDragEnd(event) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = customTobPlans.findIndex(plan => plan.plan_id === active.id);
        const newIndex = customTobPlans.findIndex(plan => plan.plan_id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(customTobPlans, oldIndex, newIndex);
        setCustomTobPlans(reordered);

        const ids = reordered.map(plan => plan.id);
        if (ids.some(id => !id)) return;

        try {
            await trackSave(updateCustomTobPlanSortingOrder({ body: { ids }, token }));
        } catch (error) {
            toast.error('Failed to save plan order.');
        }
    }

    function handleOpenRename(plan) {
        setRenamingPlan(plan);
        setRenameValue(planDisplayName(plan));
    }

    function handleCloseRename() {
        setRenamingPlan(null);
    }

    function handleSubmitRename() {
        const trimmed = renameValue.trim();
        // An empty name is not a name -- it hands the chip back to rows 1 and 2.
        const customLabel = trimmed === composedPlanName(renamingPlan) ? '' : trimmed;
        const updatedPlan = { ...renamingPlan, custom_label: customLabel };

        setCustomTobPlans(prev => prev.map(plan => (
            plan.plan_id === renamingPlan.plan_id ? updatedPlan : plan
        )));
        persistPlan(updatedPlan);
        handleCloseRename();
    }

    const header = (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h6" component="h2" {...titleProps}>My TOB Plans</Typography>
                {saveStatus === 'saving' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                        <CircularProgress size={14} thickness={5} color="inherit" />
                        <Typography variant="body2">Saving...</Typography>
                    </Box>
                )}
                {saveStatus === 'saved' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#4caf50' }}>
                        <CheckCircleIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2">Saved</Typography>
                    </Box>
                )}
            </Box>
            <Typography variant="body2" color="text.secondary">
                Add your own TOB plans here and use them later in Auto Quotation
            </Typography>

            {ready && (
                <>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1.5, mb: 1 }}>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={customTobPlans.map(plan => plan.plan_id)} strategy={horizontalListSortingStrategy}>
                                {customTobPlans.map(plan => (
                                    <SortableCustomTobChip
                                        key={plan.plan_id}
                                        plan={plan}
                                        onDelete={() => handleRequestDeleteColumn(plan)}
                                        onRename={() => handleOpenRename(plan)}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                        <Chip size="small"
                            variant="outlined"
                            icon={<AddIcon sx={{ color: '#7e57c2 !important' }} />}
                            label="Add TOB"
                            onClick={handleAddColumn}
                            sx={{ backgroundColor: '#f5f5f5', color: '#7e57c2', borderStyle: 'dashed', borderColor: '#7e57c2', borderWidth: 1 }}
                        />
                    </Box>

                    {/* <Typography color="text.secondary" sx={{ fontSize: 12, mb: 1 }}>
                        Click any plan cell to edit it, Enter to save, Esc to cancel. Shift-click another cell in the same column to select a range, Ctrl-click to add one. Drag a plan to reorder its column, or click it to rename.
                    </Typography> */}

                    <MenuBar
                        cell={selectedCellData}
                        selectedCount={selection?.rows.length || 0}
                        canMerge={canMerge}
                        canUnmerge={canUnmerge}
                        onToggleBold={() => formatSelection({ font_weight: isBoldCell(selectedCellData) ? '400' : '700' })}
                        onSetTextColor={color => formatSelection({ cell_text_color: color })}
                        onSetBackgroundColor={color => formatSelection({ cell_bg_color: color })}
                        onMerge={handleMerge}
                        onUnmerge={handleUnmerge}
                    />
                </>
            )}
        </>
    );

    const content = (
        <>
            {!ready ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <Paper sx={{ p: 2, ...(fillHeight && { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }) }}>
                    <TOBTable
                        fillHeight={fillHeight}
                        tobPlans={customTobPlans}
                        tobBenefitLabelsColumn={labelsColumn}
                        visibleRowNumbers={visibleRowNumbers}
                        editable
                        planColumnWidth={PLAN_COLUMN_WIDTH}
                        onUpdateCell={handleUpdateCell}
                        selection={selection}
                        onSelectCells={setSelection}
                    />
                </Paper>
            )}

            {/* <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(customTobPlans, null, 2)}</pre> */}
        </>
    );

    return (
        <>
            {renderLayout({ header, content })}

            <Dialog
                sx={{ '& .MuiDialog-paper': { borderRadius: 7, py: 1.5, px: 1.5, backgroundColor: '#f1f4fa' } }}
                open={Boolean(renamingPlan)}
                onClose={handleCloseRename}
                fullWidth
                maxWidth="xs">
                <DialogTitle>Rename Plan</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Plan name"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSubmitRename(); } }}
                        helperText="Leave empty to use the plan name from rows 1 and 2."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseRename}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmitRename}>Save</Button>
                </DialogActions>
            </Dialog>

            <AlertDialog
                open={Boolean(planPendingDelete)}
                setOpen={open => { if (!open) setPlanPendingDelete(null); }}
                title="Remove TOB Plan"
                content={planPendingDelete ? `Please confirm removing the plan <strong>${planDisplayName(planPendingDelete)}</strong>.` : 'Please confirm removing this plan.'}
                positiveButtonLabel="Remove"
                negativeButtonLabel="Cancel"
                callback={handleConfirmDeleteColumn}
            />
        </>
    );
}
